/**
 * NewSurveyChooserModal
 *
 * P_GUIDED_SURVEY_EXPERIENCES_1.0
 *
 * The simple two-option chooser the author sees when they click
 * "+ New Survey". Guided is presented as the recommended default;
 * the Builder remains a first-class option for power users.
 *
 * Why a chooser instead of an opinionated default route?
 * The user's product vision is that authors should think in business
 * outcomes — but power users still need the Builder. A tiny chooser
 * preserves both paths without forcing one over the other.
 */
import { useTranslation } from 'react-i18next';
import { Sparkles, Settings2, X } from 'lucide-react';

import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

const NewSurveyChooserModal = ({ open, onOpenChange, onGuided, onBlank }) => {
    const { t } = useTranslation();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg"
                data-testid="new-survey-chooser"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <DialogTitle className="text-lg" data-testid="new-survey-chooser-title">
                        {t('surveys.chooser.title', 'Start a new survey')}
                    </DialogTitle>
                </DialogHeader>

                <p className="text-sm text-slate-600 dark:text-slate-300">
                    {t('surveys.chooser.subtitle', 'Two ways to get started — pick whichever fits.')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                    {/* Guided — recommended */}
                    <button
                        type="button"
                        onClick={onGuided}
                        data-testid="chooser-guided-btn"
                        data-insight-id="surveys.action.guided-survey"
                        data-insight-label="Start a guided survey"
                        className="text-left rounded-lg border-2 border-fuchsia-400 bg-fuchsia-50/50 dark:bg-fuchsia-950/30 p-4 transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                    >
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-300" />
                            <span className="text-[10px] uppercase tracking-wide font-bold text-fuchsia-700 dark:text-fuchsia-200 bg-fuchsia-200/70 dark:bg-fuchsia-900/60 px-1.5 py-0.5 rounded">
                                {t('surveys.chooser.recommended', 'Recommended')}
                            </span>
                        </div>
                        <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
                            {t('surveys.chooser.guidedTitle', 'Guided Survey')}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                            {t('surveys.chooser.guidedDesc', 'Tell us what you want to learn — the platform builds the survey for you.')}
                        </div>
                    </button>

                    {/* Blank Builder */}
                    <button
                        type="button"
                        onClick={onBlank}
                        data-testid="chooser-blank-btn"
                        data-insight-id="surveys.action.blank-survey"
                        data-insight-label="Start a blank survey"
                        className="text-left rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4 transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400"
                    >
                        <Settings2 className="w-5 h-5 text-slate-500 dark:text-slate-300" />
                        <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
                            {t('surveys.chooser.blankTitle', 'Blank Survey')}
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                            {t('surveys.chooser.blankDesc', 'Build from scratch in the Survey Builder.')}
                        </div>
                    </button>
                </div>

                <div className="flex justify-end pt-2 mt-1 border-t border-slate-200 dark:border-slate-700">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenChange?.(false)}
                        data-testid="chooser-cancel"
                    >
                        <X className="w-3.5 h-3.5 mr-1" />
                        {t('common.cancel', 'Cancel')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NewSurveyChooserModal;
